import { useEffect, useState } from 'react'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useCustomPageStore } from 'app/stores/StoreContext'
import type { CustomPageJson, CustomPages } from 'app/types/entities'
import {
  useCustomPageCreated,
  useCustomPageDeleted,
  isDeletedPageId,
  isPendingPageId,
  acknowledgePendingPageId,
} from 'lib/customPageEvents'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'

const CUSTOM_PAGE_TYPES: CustomPages[] = [`contacts`, `tasks`, `activities`, `treatmentPlans`, `appointments`]

// Only the grouped sidebar draws these two record types, and it reads every saved list from Lists.
const GROUPED_ONLY_TYPES: CustomPages[] = [`treatmentPlans`, `appointments`]

const EMPTY: Record<CustomPages, CustomPageJson[]> = {
  contacts: [],
  tasks: [],
  activities: [],
  treatmentPlans: [],
  appointments: [],
}

// Hook to fetch all custom pages for a user
export function useCustomPages() {
  const customPageStore = useCustomPageStore()
  const groupedNavigation = useFeatureToggle(`grouped-navigation`)
  const fetchedTypes = groupedNavigation ? [] : CUSTOM_PAGE_TYPES.filter((type) => !GROUPED_ONLY_TYPES.includes(type))
  const [customPages, setCustomPages] = useState<Record<CustomPages, CustomPageJson[]>>(EMPTY)
  const [loading, setLoading] = useState<boolean>(true)

  const fetchAllCustomPages = async () => {
    try {
      setLoading(true)
      const results = await Promise.all(
        fetchedTypes.map((type) => customPageStore.fetchList({ search: `type:${type}` })),
      )
      const hitsByType = fetchedTypes.map(
        (type, index) =>
          [
            type,
            // A page just deleted may still be in the index; keep it out until the index drops it.
            (((results[index].data as any)?.customPages?.hits ?? []) as CustomPageJson[]).filter(
              (hit) => !isDeletedPageId(hit.id!),
            ),
          ] as const,
      )
      // Acknowledge any pending IDs now returned by the server
      for (const [, hits] of hitsByType) {
        for (const hit of hits) if (hit.id) acknowledgePendingPageId(hit.id)
      }
      setCustomPages((prev) => {
        const next = { ...EMPTY }
        for (const [type, hits] of hitsByType) {
          const ids = new Set(hits.map((hit) => hit.id))
          // A page just created may not be in the index yet; keep it until the server returns it.
          next[type] = [...hits, ...prev[type].filter((page) => !ids.has(page.id) && isPendingPageId(page.id!))]
        }
        return next
      })
    } catch (error) {
      logWarn(`Error fetching custom pages:`, error)
    } finally {
      setLoading(false)
    }
  }

  // Optimistically insert a newly created page into the matching list
  useCustomPageCreated((newPage) => {
    if (!newPage.id || !newPage.page) return
    const pageType = newPage.page as CustomPages
    if (!CUSTOM_PAGE_TYPES.includes(pageType)) return
    setCustomPages((prev) => {
      if (prev[pageType].some((page) => page.id === newPage.id)) return prev
      return { ...prev, [pageType]: [...prev[pageType], newPage as CustomPageJson] }
    })
  })

  useCustomPageDeleted((id) => {
    setCustomPages((prev) => {
      const next = { ...prev }
      for (const type of CUSTOM_PAGE_TYPES) next[type] = prev[type].filter((page) => page.id !== id)
      return next
    })
  })

  useEffect(() => {
    bg(fetchAllCustomPages)
  }, [groupedNavigation])

  return { customPages, loading, refetch: fetchAllCustomPages }
}
